import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ExitFullscreenIcon from '@material-ui/icons/FullscreenExit';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import HeightIcon from '@material-ui/icons/Height';
import VerticalAlignCenterIcon from '@material-ui/icons/VerticalAlignCenter';
import CloseIcon from '@material-ui/icons/Close';
import fromConfiguration from '../../configuration/fromConfiguration';
import {
  FULL_DISPLAY,
  FULL_ROW_DISPLAY,
  HIDDEN_DISPLAY,
  NORMAL_DISPLAY,
} from './GraphTile';
import './GraphDashboard.scss';

const FullWidthIcon = React.memo(() => (<HeightIcon className="grb-rotate-icon" />), () => true);
const ExitFullWidthIcon = React.memo(() => (<VerticalAlignCenterIcon className="grb-rotate-icon" />), () => true);

const createLayout = (components, configuration, viewService) => {
  const { rows } = configuration;
  if (!rows) {
    return [
      {
        key: 'defaultRow',
        components: components.map((c) => ({
          Component: fromConfiguration({ ...c, viewService }),
          id: c.id,
        })),
      },
    ];
  }
  const compByIds = {};
  components.forEach((c) => {
    if (c.id) {
      compByIds[c.id] = c;
    }
  });
  let defaultRow;
  const unusedComps = components.slice();
  const removeFromArray = (ar, o) => {
    const index = ar.indexOf(o);
    if (index >= 0) {
      ar.splice(index, 1);
      return true;
    }
    return false;
  };

  const useComponent = (id) => {
    const c = compByIds[id];
    if (!c) {
      console.error(`Unkown component with id: ${id}`);
      return null;
    }
    removeFromArray(unusedComps, c);
    return fromConfiguration({ ...c, viewService });
  };

  const parseStyle = (c) => {
    const style = {};
    if (c.grow) {
      style.flex = '1 1 auto';
    }
    if (c.minHeight) {
      style.minHeight = `${c.minHeight}px`;
    }
    if (c.minWidth) {
      style.minWidth = `${c.minWidth}px`;
    }
    if (c.width) {
      style.width = c.width;
    }
    if (c.height) {
      style.height = c.height;
    }
    if (c.flex) {
      style.flex = c.flex;
    }
    if (c.flexWrap !== undefined) {
      style.flexWrap = c.flexWrap === false
        ? 'nowrap' : ((c.flexWrap === true) && 'wrap' || c.flexWrap);
    }
    return Object.keys(style).length ? style : undefined;
  };

  const componentRows = {};
  const layout = rows.map((row, iRow) => {
    const layoutRow = {
      style: parseStyle(row),
      hasComponent: id => layoutRow.components.find(c => c.id === id),
    };
    if (!row.components) {
      defaultRow = layoutRow;
      layoutRow.key = 'defaultRow';
      // Unused components will be added later
      layoutRow.components = [];
    } else {
      layoutRow.key = `Row${iRow}`;
      layoutRow.components = row.components.map((c) => {
        const [id, style] = typeof c === 'string' ? [c] : [c.id, parseStyle(c)];
        const Component = useComponent(id);
        componentRows[id] = layoutRow;
        return { Component, style, id };
      });
    }
    return layoutRow;
  });
  if (defaultRow) {
    defaultRow.components = unusedComps.map((c) => {
      componentRows[c.id] = defaultRow;
      return {
        Component: fromConfiguration({ ...c, viewService }),
        id: c.id,
      };
    });
  }
  return { rows: layout, componentRows };
};

const getRow = (rows, id) => {
  return rows.find((row) => {
    return row.hasComponent(id);
  });
};

const makeDefaultActions = ({ onCloseTile, onChangeDisplay, displayStates, rows }) => ({
  full: {
    getTooltip: id => (displayStates.current.fullDisplayId === id ? 'Exit full display mode' : 'Full display mode'),
    getIcon: id => (displayStates.current.fullDisplayId === id ? ExitFullscreenIcon : FullscreenIcon),
    getAriaLabel: id => (displayStates.current.fullDisplayId === id ? 'exit full mode' : 'set to full mode'),
    onClick: (id) => {
      onChangeDisplay(id, displayStates.current.fullDisplayId === id ? NORMAL_DISPLAY : FULL_DISPLAY);
    },
    className: 'grb-graph-tile-display-full-button',
    getId: id => `${id}-full`,
  },
  fullRow: {
    getTooltip: id => (displayStates.current.fullRowIds[id] ? 'Exit full row display mode' : 'Full row display mode'),
    getIcon: id => (displayStates.current.fullRowIds[id] ? ExitFullWidthIcon : FullWidthIcon),
    getAriaLabel: id => (displayStates.current.fullRowIds[id] ? 'exit full row mode' : 'set to full row mode'),
    onClick: (id) => {
      onChangeDisplay(id, displayStates.current.fullRowIds[id] ? NORMAL_DISPLAY : FULL_ROW_DISPLAY);
    },
    isValid: (id) => {
      const row = getRow(rows, id);
      if (row && row.components && (row.components.length > 1)) {
        const { closed = {} } = displayStates.current;
        const activeCmps = row.components.filter(c => !closed[c.id]);
        return activeCmps.length > 1;
      }
      return false;
    },
    className: 'grb-graph-tile-display-full-row-button',
    getId: id => `${id}-full-row'`,
  },
  close: {
    tooltip: 'Close',
    icon: () => CloseIcon,
    ariaLabel: () => 'close',
    onClick: onCloseTile,
    className: 'grb-graph-tile-display-full-row-button',
    getId: id => `${id}-close`,
  },
});

const GraphDashboard = (props) => {
  const { components, configuration, children, closedTiles, onCloseTile, actions, ...rest } = props;
  const [{ rows, componentRows }] = useState(() => createLayout(components, configuration, rest.viewService));

  // Display state
  const displayStates = useRef({ fullRowIds: {}, closed: { ...closedTiles } });

  const computeDisplays = useCallback(() => {
    const { fullDisplayId, fullRowIds, closed } = displayStates.current;
    if (fullDisplayId) {
      return components.reduce((d, c) => {
        d[c.id] = c.id === fullDisplayId ? FULL_DISPLAY : HIDDEN_DISPLAY;
        return d;
      }, {});
    }
    const fullRows = Object.keys(fullRowIds).map(id => componentRows[id]);
    if (fullRows.length) {
      return components.reduce((d, c) => {
        if (fullRowIds[c.id]) {
          d[c.id] = FULL_ROW_DISPLAY;
        } else {
          d[c.id] = (fullRows.indexOf(componentRows[c.id]) >= 0) || closed[c.id]
            ? HIDDEN_DISPLAY : NORMAL_DISPLAY;
        }
        return d;
      }, {});
    }
    return components.reduce((d, c) => {
      d[c.id] = closed[c.id] ? HIDDEN_DISPLAY : NORMAL_DISPLAY;
      return d;
    }, {});
  }, [components]);

  const [displays, setDisplays] = useState(() => computeDisplays());

  useEffect(() => {
    const newKeys = Object.keys(closedTiles);
    const oldKeys = Object.keys(displayStates.current.closed);
    if (newKeys.find(k => (displayStates.current.closed[k] !== closedTiles[k]))
          || oldKeys.find(k => (displayStates.current.closed[k] !== closedTiles[k]))) {
      displayStates.current.closed = closedTiles;
      setDisplays(computeDisplays());
    }
  }, [closedTiles, computeDisplays]);

  const handleChangeDisplay = useCallback((id, display) => {
    const { fullDisplayId, fullRowIds = {}/* , closed = {} */ } = displayStates.current;
    if (display === FULL_DISPLAY) {
      displayStates.current.fullDisplayId = id;
    } else if (fullDisplayId === id) {
      displayStates.current.fullDisplayId = null;
    }
    if (display === FULL_ROW_DISPLAY) {
      displayStates.current.fullRowIds[id] = true;
    } else if (fullRowIds[id]) {
      delete fullRowIds[id];
    }
    displayStates.current.update = true; // To know if we are on an initial mount or an update
    setDisplays(computeDisplays());
  }, [computeDisplays]);

  const handleCloseItem = useCallback((id) => {
    displayStates.current.closed[id] = true;
    setDisplays(computeDisplays);
    if (onCloseTile) {
      onCloseTile(id, displayStates.current.closed);
    }
  }, [computeDisplays, onCloseTile]);

  useEffect(() => {
    if (displayStates.current.update) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [displays]);

  // Actions
  const cpActions = useMemo(() => ({
    ...makeDefaultActions({
      oncloseItem: handleCloseItem,
      onChangeDisplay: handleChangeDisplay,
      displayStates,
      rows,
    }),
    ...actions,
  }), [actions]);

  return (
    <div className={classnames('grb-graph-dashboard',
        configuration.variant && `grb-graph-dashboard-${configuration.variant}`,
        configuration.fullComponent && 'grb-graph-dashboard-full-component',
        configuration.noHeaders && 'grb-graph-dashboard-no-headers')}
    >
      {children}
      {rows.map(row => (
        <div
          key={row.key}
          className={classnames(
            'grb-graph-dashboard-row',
            displayStates.current.fullDisplayId && (row.hasComponent(displayStates.current.fullDisplayId) ? 'grb-graph-dashboard-row-expanded' : 'grb-graph-dashboard-row-collapsed'),
          )}
          style={row.style}
        >
          {row.components.map(C => (
            <C.Component
              {...rest}
              style={C.style}
              className={C.className}
              key={C.id}
              id={C.id}
              noHeader={configuration.noHeaders}
              elevation={configuration.elevation}
              display={displays[C.id]}
              onChangeDisplay={displays.length === 1 ? undefined : handleChangeDisplay}
              actions={cpActions}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

GraphDashboard.propTypes = {
  components: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
  })),
  children: PropTypes.node,
  viewService: PropTypes.shape({
    getComponent: PropTypes.func,
  }).isRequired,
  closedTiles: PropTypes.shape({}),
  onCloseTile: PropTypes.func,
  actions: PropTypes.shape({}),
  configuration: PropTypes.shape({
    variant: PropTypes.string,
    noHeaders: PropTypes.bool,
    fullComponent: PropTypes.bool,
    oneTile: PropTypes.bool,
    elevation: PropTypes.number,
    rows: PropTypes.arrayOf(PropTypes.shape({
      components: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.shape({
          id: PropTypes.string,
        }),
        PropTypes.string,
      ])),
      grow: PropTypes.bool,
    })),
  }),
};

GraphDashboard.defaultProps = {
  components: undefined,
  children: undefined,
  configuration: undefined,
  closedTiles: {},
  onCloseTile: undefined,
  actions: undefined,
};

export default GraphDashboard;
